import { memo } from 'react';
import { Check, X, Loader2 } from 'lucide-react';
import type { PendingCAItem, ReviewStatus } from './pendingCa.types';

// 권위: ARCH_DASHBOARD §4.10 본체 테이블 + 색상 규약.
// 가상화는 50건 페이지 크기 기준 불필요 (200건 상한 + 1280px 가독성). 후속 필요 시 @tanstack/react-virtual 추가.

const LOW_CONFIDENCE_THRESHOLD = 0.6;

export interface PendingCATableProps {
  items: PendingCAItem[];
  selectedIds: Set<number>;
  highlightedIds: Set<number>;
  /** 현재 행 단위 액션이 진행 중인 audit_id 집합 — 중복 클릭 방지용 (승인/반려 버튼 disable). */
  pendingRowIds?: Set<number>;
  isLoading?: boolean;
  onToggleSelect: (auditId: number) => void;
  onToggleSelectAll: (checked: boolean) => void;
  onRowClick: (item: PendingCAItem) => void;
  onApprove: (item: PendingCAItem) => void;
  onReject: (item: PendingCAItem) => void;
}

export function PendingCATable({
  items,
  selectedIds,
  highlightedIds,
  pendingRowIds,
  isLoading,
  onToggleSelect,
  onToggleSelectAll,
  onRowClick,
  onApprove,
  onReject,
}: PendingCATableProps): JSX.Element {
  const allChecked = items.length > 0 && items.every((i) => selectedIds.has(i.auditId));

  return (
    <div className="pca-table-wrap">
      <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        <table className="pca-table" data-testid="pca-table">
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input
                  type="checkbox"
                  aria-label="전체 선택"
                  checked={allChecked}
                  onChange={(e) => onToggleSelectAll(e.target.checked)}
                />
              </th>
              <th>created_at</th>
              <th>symbol</th>
              <th>법인</th>
              <th>event_type</th>
              <th>ex_date</th>
              <th>adj_factor</th>
              <th>confidence</th>
              <th>status</th>
              <th style={{ width: 180 }}>액션</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={10} className="pca-empty">
                  불러오는 중…
                </td>
              </tr>
            )}
            {!isLoading && items.length === 0 && (
              <tr>
                <td colSpan={10} className="pca-empty">
                  조건에 맞는 audit 가 없습니다.
                </td>
              </tr>
            )}
            {items.map((it) => (
              <PendingCARow
                key={it.auditId}
                item={it}
                selected={selectedIds.has(it.auditId)}
                highlighted={highlightedIds.has(it.auditId)}
                pending={pendingRowIds?.has(it.auditId) ?? false}
                onToggleSelect={onToggleSelect}
                onClick={onRowClick}
                onApprove={onApprove}
                onReject={onReject}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface RowProps {
  item: PendingCAItem;
  selected: boolean;
  highlighted: boolean;
  /** 이 행의 승인/반려 API 호출이 진행 중이면 true — 버튼 disable. */
  pending: boolean;
  onToggleSelect: (auditId: number) => void;
  onClick: (item: PendingCAItem) => void;
  onApprove: (item: PendingCAItem) => void;
  onReject: (item: PendingCAItem) => void;
}

const PendingCARow = memo(function PendingCARow({
  item,
  selected,
  highlighted,
  pending,
  onToggleSelect,
  onClick,
  onApprove,
  onReject,
}: RowProps): JSX.Element {
  const className = rowClass(item.reviewStatus, item.llmConfidence, highlighted);
  const conf = item.llmConfidence;
  const isTerminal = item.reviewStatus === 'approved' || item.reviewStatus === 'rejected';
  const disableActions = isTerminal || pending;

  return (
    <tr
      className={className}
      data-testid={`pca-row-${item.auditId}`}
      onClick={() => onClick(item)}
    >
      <td onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          aria-label={`select ${item.auditId}`}
          checked={selected}
          onChange={() => onToggleSelect(item.auditId)}
        />
      </td>
      <td>{formatKst(item.createdAt)}</td>
      <td>
        <strong>{item.symbol ?? '-'}</strong>
      </td>
      <td>{item.corpCode ?? '-'}</td>
      <td>{item.eventType ?? '-'}</td>
      <td>{item.exDate ?? '-'}</td>
      <td>{fmtNum(item.adjustmentFactor, 6)}</td>
      <td>
        <span style={conf != null && conf < LOW_CONFIDENCE_THRESHOLD ? { color: 'var(--danger)', fontWeight: 600 } : undefined}>
          {fmtNum(conf, 2)}
        </span>
      </td>
      <td>
        <span className={`pca-status-badge pca-status-badge--${item.reviewStatus}`}>
          {item.reviewStatus}
        </span>
      </td>
      <td onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="pca-btn pca-btn--primary"
          disabled={disableActions}
          onClick={() => onApprove(item)}
        >
          {pending ? (
            <Loader2 size={14} strokeWidth={1.75} className="pca-spin" />
          ) : (
            <Check size={14} strokeWidth={2} />
          )}
          {pending ? '처리 중…' : '승인'}
        </button>{' '}
        <button
          type="button"
          className="pca-btn pca-btn--danger"
          disabled={disableActions}
          onClick={() => onReject(item)}
        >
          {pending ? (
            <Loader2 size={14} strokeWidth={1.75} className="pca-spin" />
          ) : (
            <X size={14} strokeWidth={2} />
          )}
          {pending ? '처리 중…' : '반려'}
        </button>
      </td>
    </tr>
  );
});

export function rowClass(
  status: ReviewStatus,
  confidence: number | null,
  highlighted: boolean
): string {
  const classes: string[] = [];
  switch (status) {
    case 'pending_review':
    case 'auto_accepted':
      classes.push('row--pending-review');
      break;
    case 'approved':
      classes.push('row--approved');
      break;
    case 'rejected':
      classes.push('row--rejected');
      break;
    case 'edited':
      classes.push('row--edited');
      break;
    default:
      break;
  }
  // 저신뢰는 status 와 별개로 우선 적용 (row--low-confidence !important).
  if (confidence != null && confidence < LOW_CONFIDENCE_THRESHOLD && status !== 'rejected') {
    classes.push('row--low-confidence');
  }
  if (highlighted) classes.push('row--highlight');
  return classes.join(' ');
}

function fmtNum(n: number | null, digits: number): string {
  if (n == null) return '-';
  return n.toFixed(digits);
}

function formatKst(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('ko-KR', { hour12: false });
  } catch {
    return iso;
  }
}
